// src/platform/services/TenantService.js
// Complete tenant management service for Actualizer2v1 - Firebase v9 compatible

import { FirebaseService } from '../../shared/services/FirebaseService';
import {
  collection,
  doc,
  getDocs,
  getDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  runTransaction
} from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';

const CLAIM_REFRESH_ATTEMPTS = 20;
const CLAIM_REFRESH_DELAY_MS = 500;

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * TenantService - Manages multi-tenant company lifecycle
 * Handles creation, deletion, updates, and infrastructure management
 */
class TenantServiceClass {
  constructor() {
    this.firestore = null;
    this.initialized = false;
  }

  /**
   * Initialize service with Firebase instances
   */
  async initialize() {
    if (this.initialized) return;

    try {
      await FirebaseService.initialize();
      this.firestore = FirebaseService.getFirestore();
      this.initialized = true;
      
      console.log('✅ TenantService initialized successfully');
    } catch (error) {
      console.error('❌ TenantService initialization failed:', error);
      throw error;
    }
  }

  /**
   * Ensure service is initialized before operations
   */
  async ensureInitialized() {
    if (!this.initialized) {
      await this.initialize();
    }
  }

  // =============================================================================
  // TENANT CREATION
  // =============================================================================

  /**
   * Create new tenant company with full infrastructure
   */
  async createTenant(tenantData) {
    await this.ensureInitialized();

    try {
      console.log('🏢 Creating new tenant company:', tenantData);

      // 1. Validate required fields
      this.validateTenantData(tenantData);

      // 2. Derive slug (throws if name yields an empty slug — addresses
      //    PR #32 Codex/CodeRabbit finding on "!!!" / non-Latin inputs)
      const baseId = this._slugifyTenantName(tenantData.name);

      // 3. Atomic uniqueness check + write within a single Firestore
      //    transaction. Avoids the race where two concurrent createTenant
      //    calls both pass a uniqueness check before either writes
      //    (PR #32 CodeRabbit finding).
      const MAX_ATTEMPTS = 50;
      const result = await runTransaction(this.firestore, async (tx) => {
        for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt++) {
          const candidate = attempt === 0 ? baseId : `${baseId}_${attempt}`;
          const docRef = doc(this.firestore, 'companies', candidate);
          const snap = await tx.get(docRef);
          if (!snap.exists()) {
            const tenantDoc = this._buildTenantDoc(candidate, tenantData);
            tx.set(docRef, tenantDoc);
            return { tenantId: candidate, tenantDoc };
          }
        }
        throw new Error(
          `Could not find an available tenant ID after ${MAX_ATTEMPTS} attempts (baseId='${baseId}')`
        );
      });

      const { tenantId, tenantDoc } = result;
      console.log('✅ Tenant created successfully:', tenantId);

      return {
        id: tenantId,
        companyId: tenantId,
        ...tenantDoc
      };

    } catch (error) {
      console.error('❌ Tenant creation failed:', error);
      throw new Error(`Failed to create tenant: ${error.message}`);
    }
  }

  /**
   * Slugify a company name into a tenant ID candidate. Pure function:
   * lowercases, replaces non-alphanumerics with underscore, collapses
   * runs, trims, truncates, and rejects empty results.
   * @param {string} companyName
   * @returns {string} non-empty slug (max 20 chars, [a-z0-9_])
   * @throws {Error} when the name strips to an empty slug
   */
  _slugifyTenantName(companyName) {
    const baseId = String(companyName || '')
      .toLowerCase()
      .replace(/[^a-z0-9]/g, '_')
      .replace(/_+/g, '_')
      .replace(/^_|_$/g, '')
      .substring(0, 20);

    if (!baseId) {
      throw new Error(
        `Could not derive a tenant ID from name "${companyName}". ` +
        `Use a name containing at least one ASCII letter or digit.`
      );
    }
    return baseId;
  }

  /**
   * Build the Firestore document for a tenant. Extracted so the
   * createTenant transaction body stays small. Each retry within the
   * transaction re-builds the doc with the new candidate ID.
   * @param {string} tenantId
   * @param {object} tenantData
   * @returns {object} document to write to companies/{tenantId}
   */
  _buildTenantDoc(tenantId, tenantData) {
    return {
      companyId: tenantId,
      name: tenantData.name,
      description: tenantData.description || '',
      status: tenantData.status || 'active',
      adminEmail: tenantData.adminEmail,
      industry: tenantData.industry || '',
      timezone: tenantData.timezone || 'America/New_York',

      // Infrastructure details
      bigqueryDataset: `actualizer2_${tenantId}`,
      storageBucket: `actualizer2-${tenantId}`,

      // Platform metadata
      createdAt: new Date().toISOString(),
      createdBy: FirebaseService.getCurrentUser()?.email || 'system',
      version: '2.1.0',
      platform: 'actualizer2v1',

      // Usage tracking
      userCount: 0,
      dataUsageMB: 0,
      lastActivity: new Date().toISOString(),

      // Feature flags
      features: {
        chainto: tenantData.features?.chainto || false,
        analytics: tenantData.features?.analytics || true,
        reporting: tenantData.features?.reporting || true
      }
    };
  }

  // =============================================================================
  // TENANT LISTING AND RETRIEVAL
  // =============================================================================

  /**
   * List all tenant companies
   */
  async listTenants(options = {}) {
    await this.ensureInitialized();

    try {
      console.log('📋 Fetching tenant companies...');

      // Build query using v9 syntax
      let q = query(collection(this.firestore, 'companies'));

      // Apply filters
      if (options.status) {
        q = query(q, where('status', '==', options.status));
      }

      // Apply ordering
      q = query(q, orderBy('createdAt', 'desc'));

      // Apply limit
      if (options.limit) {
        q = query(q, limit(options.limit));
      }

      const querySnapshot = await getDocs(q);
      const tenants = [];

      querySnapshot.forEach(doc => {
        tenants.push({
          // Spread FIRST — same reason as getTenant. These ids are not
          // display-only: PlatformDashboard passes the selected row's
          // `.id` straight into deleteTenant (:357 -> :100), so a
          // persisted `id` field would aim the platform admin's delete
          // at the wrong tenant.
          ...doc.data(),
          id: doc.id,
          companyId: doc.id
        });
      });

      console.log(`✅ Retrieved ${tenants.length} tenants`);
      return tenants;

    } catch (error) {
      console.error('❌ Failed to list tenants:', error);
      throw new Error(`Failed to list tenants: ${error.message}`);
    }
  }

  /**
   * Get all tenants (for platform admin)
   */
  async getAllTenants() {
    await this.ensureInitialized();

    try {
      console.log('📊 TenantService: Loading all tenants...');
      
      // Use the existing listTenants method which already has Firestore integration
      const tenants = await this.listTenants();
      
      // If no tenants exist, return some sample data for testing
      if (tenants.length === 0) {
        console.log('📝 No tenants found, returning sample data for testing');
        return [
          {
            id: 'sample_1',
            companyId: 'sample_1',
            name: 'Acme Corporation',
            industry: 'Manufacturing',
            status: 'active',
            adminEmail: 'admin@acme.com',
            userCount: 25,
            dataUsageMB: 1024,
            createdAt: '2024-01-15T10:00:00Z',
            features: {
              chainto: true,
              analytics: true,
              reporting: true
            },
            bigqueryDataset: 'acme_corp_dataset',
            storageBucket: 'acme-corp-storage'
          },
          {
            id: 'sample_2',
            companyId: 'sample_2', 
            name: 'TechStart Inc',
            industry: 'Technology',
            status: 'active',
            adminEmail: 'admin@techstart.com',
            userCount: 12,
            dataUsageMB: 512,
            createdAt: '2024-02-01T14:30:00Z',
            features: {
              chainto: false,
              analytics: true,
              reporting: true
            },
            bigqueryDataset: 'techstart_dataset',
            storageBucket: 'techstart-storage'
          }
        ];
      }

      console.log(`✅ Loaded ${tenants.length} tenants`);
      return tenants;
      
    } catch (error) {
      console.error('❌ Failed to load tenants:', error);
      throw error;
    }
  }

  /**
   * Get specific tenant by ID.
   *
   * CHA-547: accepts both the bare slug ('inmocean') and the legacy
   * prefixed form ('company_inmocean'). CHA-102 stripped the `company_`
   * prefix on the roleAssignments side, so minted claims — and therefore
   * `userProfile.companyId` via App.jsx:118 — carry bare slugs. The
   * `companies` documents were never migrated and are still prefixed, so
   * a bare-slug read 404s and every tenant-scoped login died on
   * "Tenant not found: inmocean".
   *
   * This is a compatibility shim, not the migration. It lets the code
   * survive either ID shape so the ADR-0003 `companies/` migration can be
   * scheduled on its own terms rather than run under pressure. Remove the
   * fallback once that migration lands (see CHA-93 / CHA-127).
   */
  async getTenant(companyId) {
    await this.ensureInitialized();

    try {
      // Probe the canonical (bare-slug) id first so that once the data is
      // migrated the legacy branch stops being reached, and an already-
      // prefixed caller ('company_inmocean' from the platform tenant list)
      // still resolves on the first read.
      const candidates = [companyId];
      if (!companyId.startsWith('company_')) {
        candidates.push(`company_${companyId}`);
      }

      for (const candidate of candidates) {
        const docSnap = await getDoc(doc(this.firestore, 'companies', candidate));
        if (docSnap.exists()) {
          if (candidate !== companyId) {
            console.warn(
              `⚠️  Tenant "${companyId}" resolved via legacy prefixed doc "${candidate}" (CHA-547) — companies/ still needs the ADR-0003 migration`
            );
          }
          return {
            // Spread FIRST: the document's own id is authoritative, and
            // updateTenant/deleteTenant now build their docRef from it.
            // With the spread last, a persisted `id` or `companyId` field
            // would overwrite the real document id and send those writes
            // at the wrong document.
            ...docSnap.data(),
            id: docSnap.id,
            companyId: docSnap.id
          };
        }
      }

      // Report the id the caller asked for, not the last probe, so the
      // error stays honest about what was requested.
      throw new Error(`Tenant not found: ${companyId}`);

    } catch (error) {
      console.error('❌ Failed to get tenant:', error);
      throw error;
    }
  }

  // =============================================================================
  // TENANT UPDATES
  // =============================================================================

  /**
   * Update tenant information
   */
  async updateTenant(companyId, updateData) {
    await this.ensureInitialized();

    try {
      console.log('📝 Updating tenant:', companyId, updateData);

      // Validate the tenant exists, and capture the id of the document
      // that actually resolved — getTenant accepts a bare slug and may
      // have matched the legacy `company_` doc (CHA-547). Writing back
      // to the requested id would target a document that doesn't exist.
      const tenant = await this.getTenant(companyId);

      // Prepare update object
      const updates = {
        ...updateData,
        updatedAt: new Date().toISOString(),
        updatedBy: FirebaseService.getCurrentUser()?.email || 'system'
      };

      // Remove fields that shouldn't be updated
      delete updates.id;
      delete updates.companyId;
      delete updates.createdAt;
      delete updates.createdBy;
      delete updates.bigqueryDataset;
      delete updates.storageBucket;

      const docRef = doc(this.firestore, 'companies', tenant.id);
      await updateDoc(docRef, updates);

      console.log('✅ Tenant updated successfully');
      return await this.getTenant(tenant.id);

    } catch (error) {
      console.error('❌ Failed to update tenant:', error);
      throw new Error(`Failed to update tenant: ${error.message}`);
    }
  }

  /**
   * Update tenant status
   */
  async updateTenantStatus(companyId, status) {
    await this.ensureInitialized();

    try {
      console.log(`🔄 Updating tenant status: ${companyId} -> ${status}`);

      const validStatuses = ['active', 'inactive', 'suspended'];
      if (!validStatuses.includes(status)) {
        throw new Error(`Invalid status: ${status}. Must be one of: ${validStatuses.join(', ')}`);
      }

      await this.updateTenant(companyId, { status });

      // If suspending, you might want to disable access
      if (status === 'suspended') {
        console.log('⚠️ Tenant suspended - consider disabling access');
      }

      return { companyId, status };

    } catch (error) {
      console.error('❌ Failed to update tenant status:', error);
      throw error;
    }
  }

  // =============================================================================
  // TENANT DELETION
  // =============================================================================

  /**
   * Delete tenant and all associated infrastructure
   */
  async deleteTenant(companyId) {
    await this.ensureInitialized();

    try {
      console.log('🗑️ Deleting tenant and infrastructure:', companyId);

      // 1. Verify tenant exists
      const tenant = await this.getTenant(companyId);

      // 2. Delete the document that actually resolved, not the requested
      //    id — a bare slug may have matched the legacy `company_` doc
      //    (CHA-547). deleteDoc resolves silently on a missing document,
      //    so targeting the requested id here would report a successful
      //    delete while leaving the real tenant in place.
      const docRef = doc(this.firestore, 'companies', tenant.id);
      await deleteDoc(docRef);

      console.log('✅ Tenant deleted successfully');

      return {
        companyId,
        deleted: true
      };

    } catch (error) {
      console.error('❌ Failed to delete tenant:', error);
      throw new Error(`Failed to delete tenant: ${error.message}`);
    }
  }

  // =============================================================================
  // TENANT UTILITIES
  // =============================================================================

  /**
   * Get tenant statistics
   */
  async getTenantStats(companyId) {
    await this.ensureInitialized();

    try {
      const tenant = await this.getTenant(companyId);

      const stats = {
        companyId,
        userCount: tenant.userCount || 0,
        dataUsageMB: tenant.dataUsageMB || 0,
        tablesCount: 0, // Would query BigQuery in production
        documentsCount: 0, // Would query Storage in production
        lastActivity: tenant.lastActivity,
        status: tenant.status,
        createdAt: tenant.createdAt
      };

      return stats;

    } catch (error) {
      console.error('❌ Failed to get tenant stats:', error);
      throw error;
    }
  }

  /**
   * Search tenants
   */
  async searchTenants(searchTerm, options = {}) {
    await this.ensureInitialized();

    try {
      const allTenants = await this.listTenants(options);
      
      if (!searchTerm) return allTenants;

      const term = searchTerm.toLowerCase();
      return allTenants.filter(tenant => 
        tenant.name?.toLowerCase().includes(term) ||
        tenant.companyId?.toLowerCase().includes(term) ||
        tenant.adminEmail?.toLowerCase().includes(term) ||
        tenant.industry?.toLowerCase().includes(term)
      );

    } catch (error) {
      console.error('❌ Failed to search tenants:', error);
      throw error;
    }
  }
// Add these methods to your TenantService.js class

// =============================================================================
// TENANT INVITATIONS SYSTEM
// =============================================================================

/**
 * Create tenant invitation
 */
async createTenantInvitation(companyId, adminEmail, invitedBy) {
  await this.ensureInitialized();

  try {
    console.log('📧 Creating tenant invitation for:', adminEmail);
    
    // Generate invitation token
    const invitationId = `inv_${Date.now()}_${Math.random().toString(36).substring(2, 8)}`;
    
    const invitation = {
      id: invitationId,
      companyId,
      adminEmail,
      invitedBy,
      status: 'pending',
      createdAt: new Date().toISOString(),
      expiresAt: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(), // 7 days
      acceptedAt: null,
      token: invitationId // In production, use crypto.randomBytes
    };

    // Store invitation in Firestore
    const docRef = doc(this.firestore, 'tenantInvitations', invitationId);
    await setDoc(docRef, invitation);

    console.log('✅ Tenant invitation created:', invitationId);
    return invitation;

  } catch (error) {
    console.error('❌ Failed to create invitation:', error);
    throw error;
  }
}

/**
 * Get pending invitation by email
 */
async getPendingInvitation(email) {
  await this.ensureInitialized();

  try {
    console.log('🔍 Checking for pending invitation:', email);
    
    const q = query(
      collection(this.firestore, 'tenantInvitations'),
      where('adminEmail', '==', email),
      where('status', '==', 'pending')
    );

    const querySnapshot = await getDocs(q);
    
    if (querySnapshot.empty) {
      throw new Error('No pending invitation found');
    }

    const invitation = querySnapshot.docs[0];
    const invitationData = {
      id: invitation.id,
      ...invitation.data()
    };

    // Check if invitation is expired
    if (new Date(invitationData.expiresAt) < new Date()) {
      throw new Error('Invitation has expired');
    }

    // Get tenant details
    const tenant = await this.getTenant(invitationData.companyId);
    
    return {
      ...invitationData,
      tenant
    };

  } catch (error) {
    console.error('❌ No valid invitation found:', error);
    throw error;
  }
}

/**
 * Accept tenant invitation.
 *
 * CHA-97 / PR-C: routes through the `acceptPlatformAdminInvitation`
 * server callable. The callable owns validation (email match,
 * expiry, status), the roleAssignments write, the tenants.userCount
 * increment, and the invitation status update — all server-side.
 *
 * The second `userData` argument is no longer needed (the callable
 * trusts request.auth) but kept in the signature for caller
 * compatibility with InvitationAcceptance.jsx. CHA-121 will revisit.
 */
async acceptTenantInvitation(invitationId, _userData) {
  await this.ensureInitialized();

  console.log('✅ Accepting tenant invitation via callable:', invitationId);
  const functions = getFunctions();
  const acceptCallable = httpsCallable(functions, 'acceptPlatformAdminInvitation');
  const result = await acceptCallable({ invitationId });

  // Codex Round 5 P1: parallel callsite to Fix 3 in
  // UMS.acceptUserInvitation. App.handleTenantInvitationAccepted reads
  // .mode and .companyId. The callable returns
  // { ok, tenantId, roleId, acceptedAt } only. Map to the legacy
  // profile shape so the platform-admin invite flow doesn't leave
  // appMode/selectedTenant undefined post-accept.
  const currentUser = FirebaseService.getCurrentUser();
  await this.waitForAcceptedInvitationClaims(currentUser, result.data.tenantId, result.data.roleId);
  return {
    uid: currentUser.uid,
    email: currentUser.email,
    mode: 'tenant',
    role: result.data.roleId,
    roleId: result.data.roleId,
    companyId: result.data.tenantId,
    tenantId: result.data.tenantId,
  };
}

async waitForAcceptedInvitationClaims(currentUser, tenantId, roleId) {
  if (!currentUser || typeof currentUser.getIdTokenResult !== 'function') {
    throw new Error('Cannot refresh invitation claims: no signed-in user');
  }

  for (let attempt = 1; attempt <= CLAIM_REFRESH_ATTEMPTS; attempt += 1) {
    const tokenResult = await currentUser.getIdTokenResult(true);
    const claims = tokenResult.claims || {};
    if (claims.tenantId === tenantId && claims.roleId === roleId) {
      return claims;
    }
    if (attempt < CLAIM_REFRESH_ATTEMPTS) {
      await sleep(CLAIM_REFRESH_DELAY_MS);
    }
  }

  throw new Error('Invitation accepted, but tenant access is not ready yet. Please try again in a moment.');
}

/**
 * Enhanced createTenant with automatic invitation
 */
async createTenantWithInvitation(tenantData) {
  await this.ensureInitialized();

  try {
    console.log('🏢 Creating tenant with admin invitation...');
    
    // Create the tenant
    const tenant = await this.createTenant(tenantData);
    
    // Create invitation for admin
    const invitation = await this.createTenantInvitation(
      tenant.companyId,
      tenantData.adminEmail,
      FirebaseService.getCurrentUser()?.email || 'system'
    );

    console.log('✅ Tenant and invitation created successfully');
    
    return {
      tenant,
      invitation,
      invitationLink: `${window.location.origin}/invite/${invitation.token}`
    };

  } catch (error) {
    console.error('❌ Failed to create tenant with invitation:', error);
    throw error;
  }
}

/**
 * Get invitation by token (for invitation links)
 */
async getInvitationByToken(token) {
  await this.ensureInitialized();

  try {
    const docRef = doc(this.firestore, 'tenantInvitations', token);
    const docSnap = await getDoc(docRef);
    
    if (!docSnap.exists()) {
      throw new Error('Invalid invitation link');
    }

    const invitation = {
      id: docSnap.id,
      ...docSnap.data()
    };

    // Check if expired
    if (new Date(invitation.expiresAt) < new Date()) {
      throw new Error('Invitation link has expired');
    }

    if (invitation.status !== 'pending') {
      throw new Error('Invitation link already used');
    }

    // Get tenant info
    const tenant = await this.getTenant(invitation.companyId);

    return {
      ...invitation,
      tenant
    };

  } catch (error) {
    console.error('❌ Invalid invitation token:', error);
    throw error;
  }
}

  /**
   * Get platform statistics across all tenants
   */
  async getPlatformStats() {
    await this.ensureInitialized();

    try {
      const tenants = await this.listTenants();

      const stats = {
        totalTenants: tenants.length,
        activeTenants: tenants.filter(t => t.status === 'active').length,
        inactiveTenants: tenants.filter(t => t.status === 'inactive').length,
        suspendedTenants: tenants.filter(t => t.status === 'suspended').length,
        totalUsers: tenants.reduce((sum, t) => sum + (t.userCount || 0), 0),
        totalDataUsageMB: tenants.reduce((sum, t) => sum + (t.dataUsageMB || 0), 0),
        
        // Recent activity
        recentlyCreated: tenants.filter(t => {
          if (!t.createdAt) return false;
          const created = new Date(t.createdAt);
          const weekAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
          return created > weekAgo;
        }).length,

        // Industry breakdown
        industries: this.groupTenantsByIndustry(tenants)
      };

      return stats;

    } catch (error) {
      console.error('❌ Failed to get platform stats:', error);
      throw error;
    }
  }

  /**
   * Validate tenant data
   */
  validateTenantData(tenantData) {
    const required = ['name', 'adminEmail'];
    const missing = required.filter(field => !tenantData[field]);

    if (missing.length > 0) {
      throw new Error(`Missing required fields: ${missing.join(', ')}`);
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(tenantData.adminEmail)) {
      throw new Error('Invalid email format for adminEmail');
    }

    // Validate name length
    if (tenantData.name.length < 2 || tenantData.name.length > 50) {
      throw new Error('Company name must be between 2 and 50 characters');
    }
  }

  /**
   * Group tenants by industry
   */
  groupTenantsByIndustry(tenants) {
    const industries = {};
    
    tenants.forEach(tenant => {
      const industry = tenant.industry || 'Unspecified';
      industries[industry] = (industries[industry] || 0) + 1;
    });

    return industries;
  }

  /**
   * Get tenant health score
   */
  calculateTenantHealth(tenant) {
    let score = 0;

    // Status (30 points)
    if (tenant.status === 'active') score += 30;
    else if (tenant.status === 'inactive') score += 15;

    // User activity (25 points)
    if (tenant.userCount > 0) score += 25;

    // Data usage (20 points)
    if (tenant.dataUsageMB > 0) score += 20;

    // Feature utilization (15 points)
    const featuresEnabled = Object.values(tenant.features || {}).filter(Boolean).length;
    score += Math.min(featuresEnabled * 5, 15);

    // Configuration completeness (10 points)
    if (tenant.description && tenant.industry) score += 10;
    else if (tenant.description || tenant.industry) score += 5;

    return Math.min(score, 100);
  }

  /**
   * Format data usage for display
   */
  formatDataUsage(usageMB) {
    if (!usageMB || usageMB === 0) return '0 MB';
    
    if (usageMB < 1024) {
      return `${usageMB} MB`;
    } else if (usageMB < 1024 * 1024) {
      return `${(usageMB / 1024).toFixed(1)} GB`;
    } else {
      return `${(usageMB / (1024 * 1024)).toFixed(2)} TB`;
    }
  }
}

// Create singleton instance
const TenantService = new TenantServiceClass();

export { TenantService };
